import { useMemo } from 'react';
import { DefaultValues } from 'react-hook-form';
import {
  defaultBasicFilterWidgetValues,
  defaultOperandState,
  defaultPredicate,
  IFilterOperand,
  IFilterPredicate,
  IFilterWidgetState,
  ISimpleFilterPredicate,
} from '../types';
import { MaybeNil } from '../../../utils/interfaces';
import {
  FilterType,
  IFieldFieldsFragment,
  IFilterFieldsFragment,
  IPredicateFieldsFragment,
  ISimplePredicateFieldsFragment,
  OperandType,
  RelationalOperatorKey,
} from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import { UnreachableCaseError } from 'ts-essentials';
import { mapFieldValue } from './getFilterWidgetOutput';

const parseOperands = ({
  relOp,
  operands,
}: Pick<
  ISimplePredicateFieldsFragment,
  'relOp' | 'operands'
>): IFilterOperand => {
  if (!operands) {
    return defaultOperandState;
  }

  const isBetween = relOp.key === RelationalOperatorKey.Between;

  if (operands.__typename === 'ValueOperands') {
    const result: IFilterOperand = {
      type: OperandType.Value,
      fromOperand: null,
      toOperand: null,
      operands: [],
    };

    if (isBetween && operands.values.length === 2) {
      assertCondition(operands.values.length === 2);

      result.fromOperand = mapFieldValue(operands.values[0]);
      result.toOperand = mapFieldValue(operands.values[1]);
    } else {
      result.operands = operands.values
        .filter((operand) => operand !== null)
        .map(mapFieldValue);
    }

    return result;
  }

  const result: IFilterOperand = {
    type: OperandType.Field,
    fromField: null,
    toField: null,
    field: null,
  };

  const fields = operands.fields;

  if (isBetween && fields.length === 2) {
    result.fromField = fields[0];
    result.toField = fields[1];
  } else {
    result.field = fields[0];
  }

  return result;
};

const parseBasicFilterSimplePredicate = (
  predicate: ISimplePredicateFieldsFragment,
): ISimpleFilterPredicate => {
  const { field, relOp, matchCase, operands, __typename } = predicate;

  return {
    __typename,
    field,
    relOp,
    matchCase: matchCase ?? true,
    operand: parseOperands({ relOp, operands }),
  };
};

const parseBasicFilterPredicate = (
  predicate: IPredicateFieldsFragment,
): IFilterPredicate => {
  const { __typename } = predicate;
  if (__typename === 'SimplePredicate') {
    return parseBasicFilterSimplePredicate(predicate);
  } else if (__typename === 'ArrayObjectPredicate') {
    return {
      __typename,
      predicateElements: predicate.elementPredicates.map(
        parseBasicFilterSimplePredicate,
      ),
    };
  } else {
    throw new UnreachableCaseError(predicate);
  }
};

const useDefaultFilterWidgetValues = (
  defaultFilter: MaybeNil<IFilterFieldsFragment>,
  lockedField?: IFieldFieldsFragment,
): DefaultValues<IFilterWidgetState> =>
  useMemo(() => {
    if (defaultFilter) {
      if (defaultFilter.__typename === 'NamedFilter') {
        const { type } = defaultFilter;

        return {
          type,
          namedFilter: defaultFilter,
        };
      }

      const { type, conditions, logicOps, __typename } = defaultFilter;

      return {
        __typename,
        type,
        logicOps,
        conditions: conditions.map((condition) => ({
          logicOps: condition.logicOps,
          predicates: condition.predicates.map((predicate) => {
            const parsedPredicate = parseBasicFilterPredicate(predicate);
            if (
              lockedField &&
              parsedPredicate.__typename === 'SimplePredicate'
            ) {
              parsedPredicate.field = lockedField;
            }
            return parsedPredicate;
          }),
        })),
      };
    }

    if (lockedField) {
      return {
        type: FilterType.Basic,
        conditions: [
          {
            predicates: [
              {
                ...defaultPredicate,
                field: lockedField,
              },
            ],
            logicOps: [],
          },
        ],
        logicOps: [],
      };
    }

    return defaultBasicFilterWidgetValues;
  }, [defaultFilter, lockedField]);

export default useDefaultFilterWidgetValues;
